/**
 * 站点级 `Organization` JSON-LD 的纯函数层。
 *
 * `buildOrganizationLd()` 把 `GET /api/front/geo/site-profile` 返回的组织信息
 * 组装成 Requirement 3.2 定义的 14 键对象。源字段（`phone` / `email` / `address` /
 * `founded` / `areaServed` / `sameAs` / `alternateName`）为空或纯空白时**省略对应键**，
 * `phone` 为空时同时省略 `contactPoint`（需求 3.10）；被省略的键名收进返回值的
 * `omittedKeys`，供调用方记日志（需求 3.10、3.12）。
 *
 * `name`、`description`、`url`、`logo`、`@context`、`@type` 六个键恒非空——
 * 这既是需求 3.2 对 `name`/`description` 的长度约束所要求的（缺失时兜底而不是省略），
 * 也是需求 3.12「无 profile 时输出六个非空键」兜底路径的自然结果：
 * 本函数不区分「profile 整体缺失」与「profile 部分字段缺失」，两者走同一套兜底逻辑。
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读全局状态，Node 与浏览器结果一致，
 * 供运行时 `src/utils/seo.ts`、构建期 `scripts/prerender/*` 与巡检器复用。
 *
 * 对应需求：3.2、3.3、3.4、3.5、3.6、3.10、3.11、3.12、3.13
 */

import { SITE_ORIGIN, DEFAULT_OG_IMAGE } from "../constants";
import type { DateInput } from "../dates";
import { toFoundingDate } from "../dates";
import { clampJsonLdText, toAbsoluteUrl } from "./serialize";

/** `name` 兜底值：`companyName` 与 `siteName` 均为空/纯空白时使用 */
const ORG_NAME_FALLBACK = "欧格登智能科技";

/** `contactPoint[].contactType` 的默认取值：源数据无对应字段，仅生成 1 个元素时无需考虑两两不同 */
const DEFAULT_CONTACT_TYPE = "客户服务";

/** `contactPoint[].areaServed` 的兜底值：组织级 `areaServed` 为空时使用，保证该数组恒 ≥1 个非空字符串 */
const DEFAULT_CONTACT_AREA_SERVED = ["中国大陆"];

/** `contactPoint[].availableLanguage` 固定取值（需求 3.4） */
const AVAILABLE_LANGUAGE = "zh-CN";

/** `name` / `alternateName` 单项长度闭区间（需求 3.2、3.11） */
const NAME_LENGTH = { min: 2, max: 60 } as const;

/** `description` 长度闭区间（需求 3.2） */
const DESCRIPTION_LENGTH = { min: 40, max: 200 } as const;

/** `address` 三个可变键（`addressRegion`/`addressLocality`/`streetAddress`）的长度闭区间（需求 3.3） */
const ADDRESS_FIELD_LENGTH = { min: 2, max: 100 } as const;

/** `areaServed` 单项长度闭区间与元素数量上限（需求 3.11） */
const AREA_SERVED_LENGTH = { min: 2, max: 50 } as const;
const AREA_SERVED_MAX_COUNT = 20;

/** `sameAs` 元素数量上限（需求 3.11） */
const SAME_AS_MAX_COUNT = 10;

/** `Organization` JSON-LD 的 `address` 键（需求 3.3） */
export type OrganizationPostalAddress = {
  "@type": "PostalAddress";
  addressCountry: "CN";
  addressRegion: string;
  addressLocality: string;
  streetAddress: string;
};

/** `Organization` JSON-LD 的 `contactPoint` 单个元素（需求 3.4） */
export type OrganizationContactPoint = {
  "@type": "ContactPoint";
  telephone: string;
  contactType: string;
  areaServed: string[];
  availableLanguage: typeof AVAILABLE_LANGUAGE;
};

/** `buildOrganizationLd()` 输出的 14 键对象（部分键在源字段空白时被省略，需求 3.2、3.10） */
export type OrganizationLd = {
  "@context": "https://schema.org";
  "@type": "Organization";
  name: string;
  alternateName?: string[];
  url: string;
  logo: string;
  description: string;
  address?: OrganizationPostalAddress;
  telephone?: string;
  email?: string;
  areaServed?: string[];
  foundingDate?: string;
  contactPoint?: OrganizationContactPoint[];
  sameAs?: string[];
};

/** `buildOrganizationLd()` 的返回值：JSON-LD 对象与被省略的键名清单 */
export type OrganizationLdResult = {
  jsonLd: OrganizationLd;
  /** 被省略的键名，按需求 3.2 的键顺序排列，供调用方记日志（需求 3.10、3.12） */
  omittedKeys: string[];
};

/**
 * `GET /api/front/geo/site-profile` 的响应形状（design.md「Components and Interfaces」一节）。
 *
 * 全部字段可缺失：接口失败、超时或字段本身为空都按同一套兜底/省略逻辑处理，
 * 调用方无需先判空再决定是否调用本函数。
 */
export type OrganizationProfile = {
  companyName?: string | null;
  alternateName?: string | readonly string[] | null;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  founded?: DateInput;
  areaServed?: string | readonly string[] | null;
  sameAs?: string | readonly string[] | null;
  logoUrl?: string | null;
  siteName?: string | null;
  seo?: {
    title?: string | null;
    keywords?: string | null;
    description?: string | null;
  } | null;
};

function trimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** 按 code point 计长度，避免代理对（表情符号等）被算作 2（与 `src/seo/visibleText.ts` 同口径） */
function codePointLength(text: string): number {
  return Array.from(text).length;
}

/** 把字符串或字符串数组来源统一拆成候选字符串列表；其余类型返回空数组 */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string") {
    return [value];
  }
  return [];
}

/**
 * 过滤 `areaServed` 候选项：去首尾空白后长度落在 `[min, max]` 才保留，超过 `maxCount` 截断。
 * 结果长度为 0 表示该字段整体省略（需求 3.11 的 1–20 元素下限在此体现：
 * 一个有效元素都没有时，宁可省略键也不能输出违反下限的空数组）。
 */
function normalizeBoundedStringList(
  value: unknown,
  min: number,
  max: number,
  maxCount: number,
): string[] {
  const items: string[] = [];
  for (const candidate of toStringArray(value)) {
    const trimmed = candidate.trim();
    const len = codePointLength(trimmed);
    if (len >= min && len <= max) {
      items.push(trimmed);
    }
    if (items.length >= maxCount) {
      break;
    }
  }
  return items;
}

/** 过滤 `sameAs` 候选项：去首尾空白后须以 `https://` 开头，超过 `maxCount` 截断（需求 3.11） */
function normalizeSameAs(value: unknown, maxCount: number): string[] {
  const items: string[] = [];
  for (const candidate of toStringArray(value)) {
    const trimmed = candidate.trim();
    if (trimmed.startsWith("https://")) {
      items.push(trimmed);
    }
    if (items.length >= maxCount) {
      break;
    }
  }
  return items;
}

/**
 * 解析组织名称：`companyName` → `siteName` → 兜底常量，依次取第一个非空/纯空白来源，
 * 再经 `clampJsonLdText()` 归一到 2–60 字符（需求 3.2）。
 *
 * 单独导出是因为 `buildWebSiteLd()` 的 `name` 与 `publisher.name` 必须与
 * `Organization` 的 `name` 逐字符一致（需求 3.6），两处共用同一套解析逻辑
 * 才能保证不会因为各自实现而在长度截断处产生分歧。
 */
export function resolveOrganizationName(profile?: OrganizationProfile | null): string {
  const source =
    trimmedString(profile?.companyName) || trimmedString(profile?.siteName) || ORG_NAME_FALLBACK;
  return clampJsonLdText(source, NAME_LENGTH.min, NAME_LENGTH.max);
}

/**
 * 组装 `Organization` JSON-LD（需求 3.2–3.5、3.10–3.13）。
 *
 * - `name`：`companyName` → `siteName` → 兜底常量，归一到 2–60 字符
 * - `description`：`description` 字段，归一到 40–200 字符，空值用通用兜底语料补足
 * - `url`：固定为站点首页绝对地址（全站每个页面必须逐键一致，需求 3.13）
 * - `logo`：`logoUrl` 归一为绝对地址，空值回退到 `DEFAULT_OG_IMAGE`
 * - `address`：`address` 字段去首尾空白后非空才输出；三个可变键（`addressRegion` /
 *   `addressLocality` / `streetAddress`）取同一个值 —— 源数据只有一个地址字符串字段，
 *   没有省市区的独立来源，因此 `streetAddress` 与源字段逐字符一致（需求 3.5）的同时，
 *   另外两个键取同一取值以满足「非空、2–100 字符」的结构要求（需求 3.3）
 * - `telephone` / `email`：去首尾空白后非空才输出；`telephone` 为空时同时省略 `contactPoint`
 * - `areaServed`：过滤后为空则省略整个键（1–20 元素下限，需求 3.11）
 * - `foundingDate`：`toFoundingDate(founded)` 为 `null` 时省略
 * - `contactPoint`：仅当 `telephone` 非空时输出，1 个元素，`areaServed` 取组织级
 *   `areaServed`（为空时回退 `DEFAULT_CONTACT_AREA_SERVED`），保证该子数组恒非空（需求 3.4）
 * - `sameAs`：过滤后为空则省略整个键（0–10 元素上限，需求 3.11）
 * - `alternateName`：过滤后为空则省略；否则输出字符串数组（需求 3.11 允许字符串或数组，
 *   这里统一取数组形态）
 *
 * @param profile `GET /api/front/geo/site-profile` 的响应；缺失时走全部兜底路径，
 *   仅输出 `@context`/`@type`/`name`/`url`/`logo`/`description` 六个非空键（需求 3.12）
 */
export function buildOrganizationLd(profile?: OrganizationProfile | null): OrganizationLdResult {
  const omittedKeys: string[] = [];

  const name = resolveOrganizationName(profile);
  const description = clampJsonLdText(
    profile?.description,
    DESCRIPTION_LENGTH.min,
    DESCRIPTION_LENGTH.max,
  );
  const url = `${SITE_ORIGIN}/`;
  const logoSource = trimmedString(profile?.logoUrl);
  const logo = logoSource !== "" ? toAbsoluteUrl(logoSource) : DEFAULT_OG_IMAGE;

  const alternateNameList = normalizeBoundedStringList(
    profile?.alternateName,
    NAME_LENGTH.min,
    NAME_LENGTH.max,
    Number.MAX_SAFE_INTEGER,
  );
  if (alternateNameList.length === 0) {
    omittedKeys.push("alternateName");
  }

  const telephone = trimmedString(profile?.phone);
  if (telephone === "") {
    omittedKeys.push("telephone");
  }

  const email = trimmedString(profile?.email);
  if (email === "") {
    omittedKeys.push("email");
  }

  const streetAddress = trimmedString(profile?.address);
  const address: OrganizationPostalAddress | undefined =
    streetAddress !== ""
      ? {
          "@type": "PostalAddress",
          addressCountry: "CN",
          // 源数据只有一个地址字符串字段，没有省市区的独立来源：
          // 三个可变键取同一个值，`streetAddress` 由此天然满足「与 address 字段逐字符一致」（需求 3.5）
          addressRegion: streetAddress,
          addressLocality: streetAddress,
          streetAddress,
        }
      : undefined;
  if (!address) {
    omittedKeys.push("address");
  }

  const areaServedList = normalizeBoundedStringList(
    profile?.areaServed,
    AREA_SERVED_LENGTH.min,
    AREA_SERVED_LENGTH.max,
    AREA_SERVED_MAX_COUNT,
  );
  if (areaServedList.length === 0) {
    omittedKeys.push("areaServed");
  }

  const foundingDate = toFoundingDate(profile?.founded ?? null);
  if (foundingDate === null) {
    omittedKeys.push("foundingDate");
  }

  const contactPoint: OrganizationContactPoint[] | undefined =
    telephone !== ""
      ? [
          {
            "@type": "ContactPoint",
            telephone,
            contactType: DEFAULT_CONTACT_TYPE,
            areaServed: areaServedList.length > 0 ? areaServedList : DEFAULT_CONTACT_AREA_SERVED,
            availableLanguage: AVAILABLE_LANGUAGE,
          },
        ]
      : undefined;
  if (!contactPoint) {
    omittedKeys.push("contactPoint");
  }

  const sameAsList = normalizeSameAs(profile?.sameAs, SAME_AS_MAX_COUNT);
  if (sameAsList.length === 0) {
    omittedKeys.push("sameAs");
  }

  const jsonLd: OrganizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    ...(alternateNameList.length > 0 ? { alternateName: alternateNameList } : {}),
    url,
    logo,
    description,
    ...(address ? { address } : {}),
    ...(telephone !== "" ? { telephone } : {}),
    ...(email !== "" ? { email } : {}),
    ...(areaServedList.length > 0 ? { areaServed: areaServedList } : {}),
    ...(foundingDate !== null ? { foundingDate } : {}),
    ...(contactPoint ? { contactPoint } : {}),
    ...(sameAsList.length > 0 ? { sameAs: sameAsList } : {}),
  };

  return { jsonLd, omittedKeys };
}

/** `address` 键内部三个可变键的长度约束，导出供属性测试复用（需求 3.3） */
export const ORGANIZATION_ADDRESS_FIELD_LENGTH = ADDRESS_FIELD_LENGTH;
